import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";

type Tier = "Micro" | "Small" | "Medium" | "Large" | "XLarge" | "Enterprise";

interface PriceQuote {
  amountMinor: number;
  currency: string;
  interval: "month" | "year";
}

interface CatalogModule {
  moduleCode: string;
  displayName: string;
  shortDescription: string;
  standardCode: string;
  versionYear: number;
  isPurchasable: boolean;
  pricing: PriceQuote | null;
}

interface CoreFeeQuote {
  tier: Tier;
  pricing: PriceQuote;
}

interface CatalogResponse {
  modules: CatalogModule[];
  coreFee: CoreFeeQuote | null;
  effectiveTier: Tier | null;
  publishableKey: string | null;
}

const TIER_LABEL: Record<Tier, string> = {
  Micro: "Micro",
  Small: "Small",
  Medium: "Medium",
  Large: "Large",
  XLarge: "X Large",
  Enterprise: "Enterprise",
};

const formatPrice = (q: PriceQuote): string => {
  const formatter = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: q.currency,
  });
  return `${formatter.format(q.amountMinor / 100)}/${q.interval}`;
};

export function BillingUpgrade() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const moduleCode = params.get("moduleCode");
  const wasCancelled = params.get("checkout") === "cancelled";

  const [data, setData] = useState<CatalogResponse | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api<CatalogResponse>("/billing/catalogue")
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setErrors(formatApiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const target = data?.modules.find((m) => m.moduleCode === moduleCode);
  const tier = data?.effectiveTier ?? null;

  const onSubscribe = async () => {
    if (!target) return;
    setSubmitting(true);
    setErrors([]);
    try {
      const res = await api<{ url?: string; added?: boolean }>("/billing/checkout", {
        method: "POST",
        body: { moduleCode: target.moduleCode },
      });
      if (res.url) {
        window.location.href = res.url;
      } else if (res.added) {
        navigate(`/o/${slug}/billing?checkout=added`);
      } else {
        setErrors(["Unexpected response from billing endpoint"]);
        setSubmitting(false);
      }
    } catch (err) {
      setErrors(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
        >
          ← Back
        </button>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">Upgrade</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Add an ISO standard to your subscription. 14-day free trial — cancel from the billing page
          any time.
        </p>
      </div>

      {wasCancelled ? (
        <div
          role="status"
          className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
        >
          Checkout cancelled — no charge was made.
        </div>
      ) : null}

      {errors.length > 0 ? (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors[0]}
        </div>
      ) : null}

      {data === null ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : tier === null ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40">
          <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
            Set your company size first
          </h2>
          <p className="mt-1 text-sm text-amber-900 dark:text-amber-100">
            Pricing is tiered by employee count. Once you tell us how many employees you have, we
            can show you the right price.
          </p>
          <Link
            to={`/o/${slug}/settings/billing-tier`}
            className="mt-3 inline-block rounded-md bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700"
          >
            Set company size →
          </Link>
        </div>
      ) : tier === "Enterprise" ? (
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Talk to sales
          </h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Enterprise tier (over 250 employees) is not on self-serve pricing. Email{" "}
            <a
              className="text-brand-600 underline dark:text-brand-300"
              href="mailto:hello@isoclarity.co.uk?subject=Enterprise%20tier%20enquiry"
            >
              hello@isoclarity.co.uk
            </a>{" "}
            for a bespoke quote.
          </p>
        </div>
      ) : !target ? (
        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Pick a module to subscribe to:
          </p>
          <ul className="mt-3 space-y-2">
            {data.modules.map((m) => (
              <li key={m.moduleCode}>
                <Link
                  to={`/o/${slug}/billing/upgrade?moduleCode=${m.moduleCode}`}
                  className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
                >
                  {m.displayName}
                  {m.pricing ? ` — ${formatPrice(m.pricing)}` : ""}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {TIER_LABEL[tier]} tier pricing
            </p>
            <h2 className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">
              {target.displayName}
            </h2>
          </div>

          <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">
            {target.shortDescription}
          </p>

          {target.pricing && data.coreFee ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-900">
              <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                What you&apos;ll pay
              </h3>
              <ul className="mt-2 space-y-1 text-slate-700 dark:text-slate-300">
                <li className="flex justify-between">
                  <span>Platform Core Fee ({TIER_LABEL[tier]})</span>
                  <span className="font-mono">{formatPrice(data.coreFee.pricing)}</span>
                </li>
                <li className="flex justify-between">
                  <span>{target.displayName}</span>
                  <span className="font-mono">{formatPrice(target.pricing)}</span>
                </li>
                <li className="flex justify-between border-t border-slate-300 pt-1 font-semibold dark:border-slate-700">
                  <span>Total after 14-day trial</span>
                  <span className="font-mono">
                    {(() => {
                      const total = data.coreFee.pricing.amountMinor + target.pricing.amountMinor;
                      const fmt = new Intl.NumberFormat("en-GB", {
                        style: "currency",
                        currency: target.pricing.currency,
                      });
                      return `${fmt.format(total / 100)}/${target.pricing.interval}`;
                    })()}
                  </span>
                </li>
              </ul>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Prices exclude VAT. Stripe collects VAT at checkout if applicable.
              </p>
            </div>
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Pricing for this module at the {TIER_LABEL[tier]} tier has not been configured yet.
            </p>
          )}

          <ul className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
            <li>• 14-day free trial — no charge until day 15</li>
            <li>• Includes the Platform Core Fee — adds the Vault, audit log, NC/CAPA</li>
            <li>• Cancel any time from the billing page</li>
          </ul>

          <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4 dark:border-slate-700">
            <button
              type="button"
              onClick={() => void onSubscribe()}
              disabled={submitting || !target.pricing || !data.coreFee}
              className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting
                ? "Working…"
                : target.pricing && data.coreFee
                  ? "Subscribe"
                  : "Coming soon"}
            </button>
            <Link
              to={`/o/${slug}/billing`}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
            >
              Cancel
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
